// Imports from MUI
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import { Box, Button, FormHelperText, InputLabel, MenuItem, TextField } from "@mui/material";
import SelectFormComponent from "components/shared/FormComponent/SelectFormComponent";
import StyledButton from "components/shared/StyledButton";
// Imports from Components
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import Upload from "components/shared/Upload";
import { fileWeightValidator } from "helpers/importData-helpers";
import moment from "moment";
import { useEffect, useState } from "react";
// Imports from React
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
// Imports from Store
import { createCompany } from "store/adminPanelSlice";
import { RootState, useAppDispatch, useAppSelector } from "store/store";
import { sendPicture } from "store/titleStore";

// Imports from Types
import type { ICreateCompanyForm, IDistributor } from "../types";
import { IPeriodType } from "../types";
import defaultCreateContract from "./companyContract";
import { defaultCreateContractBlocksLexical, defaultCreateContractHtmlLexical } from "./companyContract";
import {
    getCreateCompanySiretField,
    validateCreateCompanySiret,
    validateCreateCompanySiretFormValue,
} from "./createCompanySiret";

export const textFieldStyles = {
    "&.MuiTextField-root": {
        margin: "auto",
        width: "350px",
    },
    ".MuiFormHelperText-root": {
        color: "rgb(211, 47, 47)",
    },
};
export const labelStyles = {
    color: "#737373",
    width: "100%",
    justifyContent: "space-between",
    fontSize: "12px",
    lineHeight: "18px",
    ".MuiTypography-root": {
        fontSize: "12px",
    },
    "&.MuiInputLabel-root": {
        fontFamily: "Poppins",
        fontStyle: "normal",
        width: "200px",
    },
};

interface CreateCompanyPanelProps {}

export const hardcodedDistributorList = [
    { value: "digital", label: "Immateriel" },
    { value: "digital", label: "Numilog" },
    { value: "digital", label: "Sodis" },
    { value: "digital", label: "Hachette Distribution" },
];

// the distributors names should be identical in these two variables
export const hardcodedDistributorObject: IDistributor[] = [
    { name: "Immateriel", distributorType: "digital" },
    { name: "Numilog", distributorType: "digital" },
    { name: "Sodis", distributorType: "digital" },
    { name: "Hachette Distribution", distributorType: "digital" },
];

const hardcodedCountryCodeList = [
    { name: "France", code: "fr-FR" },
    { name: "Belgique", code: "fr-BE" },
    { name: "Suisse", code: "fr-CH" },
    { name: "Canada", code: "fr-CA" },
    { name: "United Kingdom", code: "en-UK" },
    { name: "India", code: "en-IN" },
    { name: "Greece", code: "en-GR" },
    { name: "Territoires Français d'Outre-Mer", code: "fr-PF" },
];

const CreateCompany = (props: CreateCompanyPanelProps) => {
    const defaultValues: ICreateCompanyForm = {
        name: "",
        siret: "",
        address: "",
        city: "",
        country: "",
        countryCode: "",
        postalCode: "",
        useCompensationByDefault: 1,
        avatarUrl: "",
        publisherType: "",
        shareCapital: 0,
        legalStatus: "",
        defaultPackageCalculationMethod: "folio",
        distributorList: [],
        preferredTitleIdentifier: "concat",
        quarter: 1,
        year: new Date().getFullYear(),
        periodTypes: [
            {
                duration: 12,
                periodStart: new Date(),
            },
        ],
        authorizedPublisherSeats: 3,
    };
    const dispatch = useAppDispatch();
    const { sendPictureStatus } = useAppSelector((state) => state.titles);

    const [submitPending, setSubmitPending] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        reset,
        watch,
        setValue,
    } = useForm<ICreateCompanyForm>({ defaultValues });

    const {
        fields: allPeriodTypes,
        append,
        remove,
    } = useFieldArray({
        control,
        name: "periodTypes",
    });

    const [t] = useTranslation();

    const onSubmit = (data: ICreateCompanyForm) => {
        setSubmitPending(true);

        if (!data.countryCode) {
            toast.error("Code pays requis");
            setSubmitPending(false);
            return;
        }

        if (data.siret && !validateCreateCompanySiret(data.siret, data.countryCode)) {
            toast.error(`${t("InvalidSiretFormat")}`);
            setSubmitPending(false);
            return;
        }

        let companyContract = "";
        let companyContractBlocks;

        if (["press", "audiovisual"].includes(data.publisherType)) {
            companyContract = defaultCreateContractHtmlLexical
                .replace("[legalStatus]", data.legalStatus)
                .replace("[shareCapital]", data.shareCapital.toString())
                .replace("[address]", data.address)
                .replace("[postalCode]", data.postalCode)
                .replace("[city]", data.city);

            companyContractBlocks = defaultCreateContractBlocksLexical;
            companyContractBlocks.root.children[0].children[0].text =
                companyContractBlocks.root.children[0].children[0].text
                    .replace("[legalStatus]", data.legalStatus)
                    .replace("[shareCapital]", data.shareCapital.toString())
                    .replace("[address]", data.address)
                    .replace("[postalCode]", data.postalCode)
                    .replace("[city]", data.city);
        }

        const distributorSettingContent = JSON.stringify(
            data?.distributorList?.map((item: number) =>
                hardcodedDistributorObject.find((jtem) => jtem.name == hardcodedDistributorList[item].label),
            ),
        );

        // If press, start declarations from current trimester.
        if (["press", "audiovisual"].includes(data.publisherType)) {
            data.year = new Date().getFullYear();
            data.quarter = moment().quarter();
        }

        const request = {
            avatarUrl: data.avatarUrl,
            name: data.name,
            siret: data.siret,
            address: data.address,
            city: data.city,
            country: data.country,
            countryCode: data.countryCode,
            postalCode: data.postalCode,
            publisherType: data.publisherType,
            useCompensationByDefault: data.useCompensationByDefault ? data.useCompensationByDefault : 0,
            defaultPackageCalculationMethod: data.defaultPackageCalculationMethod,
            quarter: data.quarter,
            year: data.year,
            periodTypes: data.periodTypes?.map((periodType, idx) => {
                // Send UTC midnight of the local calendar day so the backend stores
                // the intended day regardless of the user's timezone.
                const local = periodType.periodStart ? new Date(periodType.periodStart) : new Date();
                return {
                    ...periodType,
                    periodStart: new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate())),
                };
            }),
            settings: [] as { name: string; content: string | number | boolean }[],
            agreementTemplate: undefined as string | undefined,
        };

        // If the publisher is a book or a press, we need to add the authorized publisher seats
        if (["book", "press", "audiovisual"].includes(data.publisherType)) {
            // `Infinity` as a string passed in the `Number` function returns `Infinity` as a number
            if (isNaN(Number(data.authorizedPublisherSeats))) {
                toast("Authorized publisher seats must be a number or 'Infinity'", { type: "error" });
                return;
            }

            // The seats number is good, we can add it to the settings
            request.settings.push({
                name: "authorized_publisher_seats",
                content: `${data.authorizedPublisherSeats}`,
            });

            request.settings.push({
                name: "uses_minimum_guaranteed_payment",
                content: data.defaultPackageCalculationMethod === "minimum_guaranteed",
            });
        }

        if (request.publisherType === "book" && request.periodTypes.length === 0) {
            toast("Must have at least one period type");
            return;
        }

        if (request.publisherType === "book")
            request.settings.push({
                name: "available_distributors",
                content: distributorSettingContent,
            });

        if (request.publisherType === "book")
            request.settings.push({ name: "uses_simultaneous_period", content: request.periodTypes.length > 1 });

        if (["press", "audiovisual"].includes(request.publisherType)) {
            request.agreementTemplate = JSON.stringify({
                blocks: companyContractBlocks,
                html: companyContract,
            });
        }
        // request.settings.push({
        //     name: "contract_agreement_template",
        //     content: JSON.stringify({ blocks: "", html: companyContract }),
        // });
        if (["press", "audiovisual"].includes(request.publisherType))
            request.settings.push({
                name: "preferred_title_identifier",
                content: data.preferredTitleIdentifier as string,
            });

        dispatch(createCompany(request)).then((response) => {
            response?.payload && reset(defaultValues);
            if (response.meta.requestStatus === "fulfilled") {
                setSubmitPending(false);
            } else {
                setSubmitPending(false);
            }
        });
    };

    const watchAvatar = watch("avatarUrl");
    useEffect(() => {
        const fetchAvatar = async (fileList: FileList) => {
            const sizeError = fileWeightValidator(fileList[0]);
            if (sizeError) {
                toast.error(`${t("Settings.FileError", { size: "1" })}`);
            } else {
                dispatch(sendPicture(fileList[0])).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                        setValue("avatarUrl", res.payload as string);
                    }
                    if (res.meta.requestStatus === "rejected") {
                        setValue("avatarUrl", "");
                    }
                });
            }
        };

        if (typeof watchAvatar !== "string") {
            fetchAvatar(watchAvatar);
        }
    }, [dispatch, setValue, watchAvatar]);

    const watchPublisherType = watch("publisherType");
    const watchCountryCode = watch("countryCode");
    const siretField = getCreateCompanySiretField(watchCountryCode, t);

    useEffect(() => {
        if (watchPublisherType === "audiovisual") {
            setValue("preferredTitleIdentifier", "unique");
            setValue("defaultPackageCalculationMethod", "amount");
        }
    }, [watchPublisherType]);

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    rowGap: "1rem",
                }}
                component="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <Controller
                    name="avatarUrl"
                    control={control}
                    render={({ field }) => (
                        <div className="input-group-form">
                            <InputLabel sx={labelStyles} htmlFor="photo">
                                Logo
                            </InputLabel>
                            <div className="upload-image-element">
                                <Upload {...field} watchAvatar={watchAvatar} />
                                {watch("avatarUrl") !== "" && (
                                    <LoadingButton
                                        loading={sendPictureStatus === "pending"}
                                        onClick={() => setValue("avatarUrl", "")}
                                        sx={{
                                            height: "fit-content",
                                            color: "black",
                                        }}
                                    >
                                        {sendPictureStatus !== "pending" && <RefreshIcon sx={{ color: "black" }} />}
                                    </LoadingButton>
                                )}
                            </div>
                        </div>
                    )}
                />
                <Controller
                    name="name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { ref, ...rest } }) => (
                        <div className="input-group-form">
                            <InputLabel sx={labelStyles} htmlFor="name">
                                {t("AdminPanel.NameCompany")}*
                            </InputLabel>
                            <StyledTextField
                                helperText={errors.name ? errors.name.message : false}
                                {...rest}
                                inputRef={ref}
                                sx={textFieldStyles}
                                placeholder={t("AdminPanel.NameCompany")}
                            />
                        </div>
                    )}
                />
                <Controller
                    name="country"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                        <div className="input-group-form">
                            <InputLabel sx={labelStyles} htmlFor="country">
                                {t("AdminPanel.Country")}*
                            </InputLabel>
                            <StyledTextField
                                helperText={errors.country ? errors.country.message : false}
                                {...rest}
                                inputRef={ref}
                                sx={textFieldStyles}
                                placeholder={t("AdminPanel.Country")}
                            />
                        </div>
                    )}
                />
                <Controller
                    name="countryCode"
                    control={control}
                    rules={{ required: "Code pays requis" }}
                    render={({ field }) => (
                        <div className="input-group-form">
                            <InputLabel sx={labelStyles} htmlFor="countryCode">
                                Code pays*
                            </InputLabel>
                            <TextField {...field} id="countryCode" select error={Boolean(errors.countryCode)}>
                                {hardcodedCountryCodeList.map((item, i) => (
                                    <MenuItem key={i} value={item.code}>
                                        {item.name} ({item.code})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                    )}
                />
                <Controller
                    name="siret"
                    control={control}
                    rules={{
                        validate: (value) => validateCreateCompanySiretFormValue(value, watchCountryCode, t),
                    }}
                    render={({ field: { ref, ...rest } }) => (
                        <div className="input-group-form">
                            <InputLabel sx={labelStyles} htmlFor="siret">
                                {siretField.label}*
                            </InputLabel>
                            <div className="input-group-form-field">
                                <StyledTextField
                                    helperText={errors.siret ? errors.siret.message : false}
                                    {...rest}
                                    inputRef={ref}
                                    sx={{
                                        "&.MuiTextField-root": {
                                            margin: 0,
                                            width: "100%",
                                        },
                                        ".MuiFormHelperText-root": {
                                            color: "rgb(211, 47, 47)",
                                        },
                                    }}
                                    placeholder={siretField.placeholder}
                                    disabled={!watchCountryCode}
                                />
                                {siretField.description && !errors.siret && (
                                    <FormHelperText
                                        sx={{
                                            color: "#737373",
                                            fontSize: "11px",
                                            ml: "4px",
                                            mt: "4px",
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {siretField.description}
                                    </FormHelperText>
                                )}
                            </div>
                        </div>
                    )}
                />
                <Controller
                    name="address"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                        <div className="input-group-form">
                            <InputLabel sx={labelStyles} htmlFor="address">
                                {t("AdminPanel.StreetNumber")}*
                            </InputLabel>
                            <StyledTextField
                                helperText={errors.address ? errors.address.message : false}
                                {...rest}
                                inputRef={ref}
                                sx={textFieldStyles}
                                placeholder={t("AdminPanel.StreetNumber")}
                            />
                        </div>
                    )}
                />
                <Controller
                    name="postalCode"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                        <div className="input-group-form">
                            <InputLabel sx={labelStyles} htmlFor="postalCode">
                                {t("AdminPanel.ZIPCode")}*
                            </InputLabel>
                            <StyledTextField
                                helperText={errors.postalCode ? errors.postalCode.message : false}
                                {...rest}
                                inputRef={ref}
                                sx={textFieldStyles}
                                placeholder={t("AdminPanel.ZIPCode")}
                            />
                        </div>
                    )}
                />
                <Controller
                    name="city"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                        <div className="input-group-form">
                            <InputLabel sx={labelStyles} htmlFor="city">
                                {t("AdminPanel.City")}*
                            </InputLabel>
                            <StyledTextField
                                helperText={errors.city ? errors.city.message : false}
                                {...rest}
                                inputRef={ref}
                                sx={textFieldStyles}
                                placeholder={t("AdminPanel.City")}
                            />
                        </div>
                    )}
                />
                <Controller
                    name="publisherType"
                    control={control}
                    render={({ field }) => (
                        <div className="input-group-form">
                            <InputLabel sx={labelStyles} htmlFor="publisherType">
                                {t("AdminPanel.PublisherType")}*
                            </InputLabel>
                            <TextField {...field} id="publisherType" select>
                                <MenuItem value={"book"}>{t("AdminPanel.Book")}</MenuItem>
                                <MenuItem value={"press"}>{t("AdminPanel.Press")}</MenuItem>
                                <MenuItem value={"audiovisual"}>{t("AdminPanel.Audiovisual")}</MenuItem>
                                <MenuItem value={"urssaf"}>{t("AdminPanel.Urssaf")}</MenuItem>
                            </TextField>
                        </div>
                    )}
                />

                <div
                    style={{
                        display: ["book", "press", "audiovisual"].includes(watchPublisherType) ? "block" : "none",
                    }}
                >
                    <Controller
                        name="authorizedPublisherSeats"
                        control={control}
                        render={({ field: { ref, ...rest } }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles}>Nombre d&apos;utilisateurs maximum*</InputLabel>
                                <StyledTextField
                                    helperText={
                                        errors.authorizedPublisherSeats
                                            ? errors.authorizedPublisherSeats.message
                                            : false
                                    }
                                    {...rest}
                                    inputRef={ref}
                                    sx={textFieldStyles}
                                    placeholder={"Infinity"}
                                />
                            </div>
                        )}
                    />
                </div>

                <div className="book-editor-info" style={{ display: watchPublisherType == "book" ? "block" : "none" }}>
                    <Controller
                        name="useCompensationByDefault"
                        control={control}
                        render={({ field }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="useCompensationByDefault">
                                    {t("AdminPanel.DefaultCompensation")}
                                </InputLabel>
                                <TextField {...field} id="outlined-select-currency" select>
                                    <MenuItem value={0}>No</MenuItem>
                                    <MenuItem value={1}>Yes</MenuItem>
                                </TextField>
                            </div>
                        )}
                    />
                    <InputLabel sx={labelStyles} htmlFor="periodStart">
                        Types de période
                    </InputLabel>
                    <div>
                        <StyledButton
                            buttonType="outlined"
                            size="small"
                            style={{ float: "right" }}
                            onClick={() => append({ duration: 12, periodStart: new Date() })}
                        >
                            Ajouter une nouvelle période
                        </StyledButton>
                    </div>
                    <div style={{ clear: "both" }} />
                    {/** Have fun with these float right things :) */}
                    {allPeriodTypes.map((periodType, index) => (
                        <div key={index} style={{ background: "#FAFAFA" }}>
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="periodStart">
                                    Type {index + 1}
                                </InputLabel>
                                <div>
                                    <StyledButton
                                        buttonType="outlined"
                                        size="small"
                                        style={{ float: "right" }}
                                        onClick={() => remove(index)}
                                    >
                                        Supprimer Type {index + 1}
                                    </StyledButton>
                                </div>
                            </div>
                            <Controller
                                name={`periodTypes.${index}.periodStart`}
                                control={control}
                                rules={{
                                    validate: (date) =>
                                        date?.toString() === "Invalid Date" ? t("NewTitle.IncorrectDate") : true,
                                }}
                                render={({ field: { ref, ...rest } }) => (
                                    <StyledDatePicker
                                        {...rest}
                                        ref={ref}
                                        inputFormat="dd/MM/yyyy"
                                        renderInput={(params) => (
                                            <div className="input-group-form">
                                                <InputLabel sx={labelStyles} htmlFor="periodStart">
                                                    {t("AdminPanel.PeriodStart")}
                                                </InputLabel>
                                                <StyledTextField
                                                    // helperText={errors.periodStart ? errors.periodStart.message : false}
                                                    sx={textFieldStyles}
                                                    {...params}
                                                />
                                            </div>
                                        )}
                                    />
                                )}
                            />
                            <Controller
                                name={`periodTypes.${index}.duration`}
                                control={control}
                                rules={{
                                    required: t("NewTitle.Required"),
                                    validate: (number) =>
                                        !isNaN(+String(number)) ? true : t("NewTitle.IncorrectDataFormat"),
                                }}
                                render={({ field: { ref, ...rest } }) => (
                                    <div className="input-group-form">
                                        <InputLabel sx={labelStyles} htmlFor="periodDuration">
                                            {t("AdminPanel.PeriodDuration")}
                                        </InputLabel>
                                        <StyledTextField
                                            // helperText={errors.periodDuration ? errors.periodDuration.message : false}
                                            sx={{ ...textFieldStyles, maxWidth: "100%" }}
                                            {...rest}
                                            inputRef={ref}
                                            placeholder="12"
                                        />
                                    </div>
                                )}
                            />
                        </div>
                    ))}
                    <div className="input-group-form">
                        <InputLabel sx={{ ...labelStyles, minWidth: "500px" }} htmlFor="distrbutorList">
                            {t("AdminPanel.DistributorList")}
                        </InputLabel>
                        <SelectFormComponent
                            control={control}
                            controlName="distributorList"
                            items={hardcodedDistributorList}
                            multiple
                        />
                    </div>
                </div>
                <div
                    className="press-editor-info"
                    style={{ display: ["press", "audiovisual"].includes(watchPublisherType) ? "block" : "none" }}
                >
                    <Controller
                        name="legalStatus"
                        control={control}
                        render={({ field }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="country">
                                    {t("AdminPanel.LegalStatus")}*
                                </InputLabel>
                                <TextField {...field} select>
                                    <MenuItem value={"Société à responsabilité limitée"}>
                                        Société à responsabilité limitée
                                    </MenuItem>
                                    <MenuItem value={"Société anonyme"}>Société anonyme</MenuItem>
                                    <MenuItem value={"Société par actions simplifiée"}>
                                        Société par actions simplifiée
                                    </MenuItem>
                                    <MenuItem value={"Société par actions simplifiée unipersonnelle"}>
                                        Société par actions simplifiée unipersonnelle
                                    </MenuItem>
                                    <MenuItem value={"Société en nom collectif"}>Société en nom collectif</MenuItem>
                                    <MenuItem value={"Société coopérative de production"}>
                                        Société coopérative de production
                                    </MenuItem>
                                    <MenuItem value={"Société en commandite par actions"}>
                                        Société en commandite par actions
                                    </MenuItem>
                                    <MenuItem value={"Société en commandite simple"}>
                                        Société en commandite simple
                                    </MenuItem>
                                    <MenuItem value={"Association à but non lucratif"}>
                                        Association à but non lucratif
                                    </MenuItem>
                                    <MenuItem value={"Entreprise individuelle"}>Entreprise individuelle</MenuItem>
                                    <MenuItem value={"Entreprise unipersonnelle à responsabilité limitée"}>
                                        Entreprise unipersonnelle à responsabilité limitée
                                    </MenuItem>
                                </TextField>
                            </div>
                        )}
                    />
                    <Controller
                        name="shareCapital"
                        control={control}
                        render={({ field: { ref, ...rest } }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="country">
                                    {t("AdminPanel.ShareCapital")}*
                                </InputLabel>
                                <StyledTextField
                                    helperText={errors.shareCapital ? errors.shareCapital.message : false}
                                    {...rest}
                                    inputRef={ref}
                                    sx={textFieldStyles}
                                    placeholder={t("AdminPanel.ShareCapital")}
                                />
                            </div>
                        )}
                    />
                    <Controller
                        name="defaultPackageCalculationMethod"
                        control={control}
                        render={({ field }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="country">
                                    {t("AdminPanel.DefaultPackage")}*
                                </InputLabel>
                                <TextField {...field} select disabled={watchPublisherType === "audiovisual"}>
                                    <MenuItem value={"folio"}>Folio</MenuItem>
                                    <MenuItem value={"amount"}>Montant</MenuItem>
                                    <MenuItem value={"minimum_guaranteed"}>Minimum garanti</MenuItem>
                                </TextField>
                            </div>
                        )}
                    />
                    <Controller
                        name="preferredTitleIdentifier"
                        control={control}
                        render={({ field }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="country">
                                    {t("AdminPanel.PreferredTitleIdentifier")}*
                                </InputLabel>
                                <TextField {...field} select disabled={watchPublisherType === "audiovisual"}>
                                    <MenuItem value={"unique"}>Code unique</MenuItem>
                                    <MenuItem value={"concat"}>Concaténer ISSN et Code de parution</MenuItem>
                                </TextField>
                            </div>
                        )}
                    />
                </div>

                <div
                    className="urssaf-editor-info"
                    style={{ display: ["urssaf", "book"].includes(watchPublisherType) ? "block" : "none" }}
                >
                    <Controller
                        name="quarter"
                        control={control}
                        render={({ field }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="quarter">
                                    {watchPublisherType === "urssaf"
                                        ? "Trimestre*"
                                        : "1er trimestre de déclaration URSSAF"}
                                </InputLabel>
                                <TextField {...field} select required>
                                    <MenuItem value={0}>Annuel</MenuItem>
                                    <MenuItem value={1}>Trimestre 1</MenuItem>
                                    <MenuItem value={2}>Trimestre 2</MenuItem>
                                    <MenuItem value={3}>Trimestre 3</MenuItem>
                                    <MenuItem value={4}>Trimestre 4</MenuItem>
                                </TextField>
                            </div>
                        )}
                    />
                    <Controller
                        name="year"
                        control={control}
                        render={({ field }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="year">
                                    {watchPublisherType === "urssaf" ? "Année*" : "1ère année de déclaration URSSAF"}
                                </InputLabel>
                                <TextField {...field} select required>
                                    {[-1, 0, 1, 2, 3, 4, 5].map((item, i) => (
                                        <MenuItem key={i} value={new Date().getFullYear() + item}>
                                            {new Date().getFullYear() + item}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </div>
                        )}
                    />
                </div>

                <StyledButton buttonType="primary" type="submit" loading={submitPending}>
                    Create Company
                </StyledButton>
            </Box>
        </>
    );
};
export default CreateCompany;
